import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { FlowManagerService } from './flowmanager.service';
import { QuestionnaireService } from './questionnaire.service';
import { ApplicationProxyService } from './applicationproxy.service';

import { GoalSession, RequiredInput } from '../models/Recommendation/RecommendationInterfaces';

import { GoalBase as GOAL_TYPE_MODEL } from '../models/Base/Goal';
import { TimeHorizonsBase as TIME_HORIZON_TYPE_MODEL } from '../models/Base/TimeHorizons';
import { RetirementBase as RETIREMENT_TYPE_MODEL } from '../models/Base/Retirement';
import { RiskToleranceBase as RISK_TYPE_MODEL } from '../models/Base/RiskTolerance';
import { StrategyTypeBase as STRATEGY_TYPE_MODEL } from '../models/Base/StrategyType';
import { CashAllocationBase as CASH_ALLOCATION_MODEL } from '../models/Base/CashAllocation';

@Injectable()
export class GoalValidationService {

  constructor(flowManager: FlowManagerService,
    questionnaire: QuestionnaireService,
    appProxy: ApplicationProxyService,
    router: Router) {
    this.flowManager = flowManager;
    this.questionnaire = questionnaire;
    this.appProxy = appProxy;
    this.router = router;
  }

  // One final check
  validateGoalSession() {
    const errors = [];
    const requiredInputs: RequiredInput[] = [{ input: 'goal', options: GOAL_TYPE_MODEL },
    { input: 'timeHorizon', options: TIME_HORIZON_TYPE_MODEL },
    { input: 'riskTolerance', options: RISK_TYPE_MODEL },
    { input: 'liquidAssets', options: null },
    { input: 'initialInvestment', options: null },
    { input: 'strategy', options: STRATEGY_TYPE_MODEL },
    { input: 'cashAllocation', options: CASH_ALLOCATION_MODEL }];

    const goalSession: GoalSession = this.flowManager.goalSession;

    if (goalSession == null) {
      errors.push({ input: 'goalSession', message: 'Goal session is null or undefined' });
    }

    if (!goalSession.liquidAssets >= 100) {
      errors.push({ input: 'liquidAssets', message: 'Liquid assets must be >= 100' });
    }
    if (!goalSession.initialInvestment >= 100) {
      errors.push({ input: 'initialInvestment', message: 'Initial investment must be >= 100' });
    }

    // Liquid Assets must be greater than initial investment
    if (!+goalSession.liquidAssets >= +goalSession.initialInvestment) {
      errors.push({ input: 'liquidAssets', message: 'Liquid assets must be greater than initial investment' });
    }

    // Check for any undefined or null values
    // And  any values that do not match what is available
    requiredInputs.forEach(x => {
      if (goalSession[x.input] == null) {
        errors.push({ input: x.input, message: 'Value is null or undefined' });
        return;
      }
      if (x.options && !(x.options.map(x => (x.key)).includes(goalSession[x.input]))) {
        errors.push({ input: x.input, message: 'Value is not an option' });
      }
    });

    // Do something with this knowledge
    if (errors.length !== 0) {
      console.error('Errors:', errors);
    }
    return errors;
  }

  prepGoalForSubmission() {
    const goalSession: GoalSession = this.flowManager.goalSession;
    const updatedGoalSession: GoalSession = { ...goalSession };

    // User deviated from our recommendation by adjusting Tier
    if (goalSession.isUserSelectedPortfolio === true) {
      // Imagine using Y/N instead of true/false in any form of programming
      updatedGoalSession.riskChangeDisclaimer = 'Y';
    }

    // User deviated from their recommended tier
    if (goalSession.isUserSelectedPortfolio === true) {
      // Both values should be present
      if (!(goalSession.userPortfolioSelection && goalSession.portfolioTier)) {
        console.error('goal session missing values for isUserSelectedPortfolio.');
      }
    } else {
      updatedGoalSession.userPortfolioSelection = null;
    }

    if (this.validateGoalSession().length !== 0) {
      return false;
    } else {
      this.flowManager.goalSession = { ...this.convertToDbValues(updatedGoalSession) };
      return this.createGoalSessionPayLoad();
    }
  }

  // Convert to proper DB values
  convertToDbValues(updatedGoalSession: GoalSession): GoalSession {
    // Time Horizon stores a different value vs what is sent up to the API
    // just to create confusion

    this.flowManager.accountTaxType = updatedGoalSession.accountTaxType;

    const convertedValues = {
      accountTaxType: RETIREMENT_TYPE_MODEL.find(x => x.key === updatedGoalSession.accountTaxType).apiValue,
      riskTolerance: RISK_TYPE_MODEL.find(x => x.key === updatedGoalSession.riskTolerance).apiValue,
      timeHorizon: TIME_HORIZON_TYPE_MODEL.find(x => x.key === updatedGoalSession.timeHorizon).dbValue,
      goalType: GOAL_TYPE_MODEL.find(x => x.key === updatedGoalSession.goal).apiValue
    };

    return Object.assign(updatedGoalSession, convertedValues);
  }

  createGoalSessionPayLoad() {
    const formData: GoalSession = this.flowManager.goalSession;

    const data = {
      promo: this.flowManager.promoCode,
      goalSessionID: formData.sessionId,
      riskTolerance: formData.riskTolerance,
      timeHorizon: formData.timeHorizon,
      accountTaxType: formData.accountTaxType,
      contributionAmount: 0,
      contributionFrequency: formData.contributionFrequency,
      goalName: formData.goal,
      goalType: formData.goalType,
      initialInvestment: formData.initialInvestment,
      investableAssets: formData.liquidAssets,
      // User deviated from their recommended strategy or tier
      riskChangeDisclaimer: formData.riskChangeDisclaimer,
      // User deviated from their recommended tier
      // What they picked
      userPortfolioSelection: formData.userPortfolioSelection,
      // What we recommended
      portfolioTier: formData.portfolioTier,
      // User deviated from their recommended strategy
      // What they picked
      strategyId: formData.strategyId,
      // What we recommended
      recommendedStrategyId: formData.recommendedStrategyId
    };

    const payload = {
      accountTaxType: data.accountTaxType,
      promo: data.promo,
      goalSessionData: this.buildGoalSession(data)
    };

    return payload;
  }

  buildGoalSession(data) {
    return {
      GoalSessionVariables: [
        {
          Step: 1,
          Name: 'portfolioTier',
          Value: data.portfolioTier
        },
        {
          Step: 1,
          Name: 'userPortfolioSelection',
          Value: data.userPortfolioSelection
        },
        {
          Step: 1,
          Name: 'riskTolerance',
          Value: data.riskTolerance
        },
        {
          Step: 1,
          Name: 'timeHorizon',
          Value: data.timeHorizon
        },
        {
          Step: 1,
          Name: 'goalType',
          Value: data.goalType
        },
        {
          Step: 1,
          Name: 'goalName',
          Value: data.goalName
        },
        {
          Step: 1,
          Name: 'saved',
          Value: data.initialInvestment
        },
        {
          // Age is no longer used however we need to maintain until the API is updated.
          // Hardcoded to 0 intentionally.
          Step: 1,
          Name: 'age',
          Value: 0
        },
        {
          Step: 1,
          Name: 'investableAssets',
          Value: data.investableAssets
        },
        {
          Step: 1,
          Name: 'riskChangeDisclaimer',
          Value: data.riskChangeDisclaimer
        },
        {
          Step: 1,
          Name: 'goalCost',
          Value: 0
        },
        {
          Step: 1,
          Name: 'accountType',
          Value: data.accountTaxType
        },
        {
          Step: 1,
          Name: 'strategyId',
          Value: data.strategyId
        },
        {
          Step: 1,
          Name: 'recommendedStrategyId',
          Value: data.recommendedStrategyId
        }
        // Remove properties if null
      ].filter(x => x.Value != null)
    };
  }

  /**
  * The only method that should be public
  * Validates goal, updates DB, and moves to OLA application
  */
  updateGoalSession() {
    return new Promise((resolve, reject) => {
      this.appProxy.updateGoalSession(this.prepGoalForSubmission())
        .catch(e => reject(e))
        .then(resp => {
          if (resp.statusCode === 'SUCCESS') {
            this.flowManager.completedQuestionnaire = true;
            resolve(resp);
          } else {
            reject(resp);
          }
        });
    });
  }
}
